import { hash } from '@ember/helper';
import SearchInput from 'tio-reimagined/components/search-input';
import { Checkbox } from 'tio-ui/components/forms';
import Select from 'tio-reimagined/components/select';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { type IntlService } from 'ember-intl';
import Component from '@glimmer/component';
import ENV from 'tio-employee/config/environment';
import pageTitle from 'ember-page-title/helpers/page-title';
import Paginator from 'tio-reimagined/components/paginator';
import SearchPalette from 'tio-common/components/search/palette';
import TasApprovalQueueRow from 'tio-common/components/tas/approval/queue-row';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type { SearchModelWith } from 'tio-common/models/search';
import type { TasProgramInstance as TasProgramInstanceResult } from 'tio-common/models/search';
import type AdminTasApprovalQueueController from 'tio-employee/controllers/authenticated/admin/tas/approval-queue';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type CableService from 'tio-common/services/cable';
import type MultiDocumentAppearanceService from 'tio-common/services/multi-document-appearance';
import type { MultiDocumentAppearanceMap } from 'tio-common/services/multi-document-appearance';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';

interface S {
  Args: {
    controller: AdminTasApprovalQueueController;
    model: SearchModelWith<TasProgramInstanceResult>;
  };
}

export default class TasApprovalQueueComponent extends Component<S> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare cable: CableService;
  @service('multi-document-appearance')
  declare multiDocAppearance: MultiDocumentAppearanceService;

  @tracked programsDisabled = false;
  @tracked selectedProgramKeys!: string[];
  @tracked showDeleteConfirmation = false;
  @tracked presenceData: MultiDocumentAppearanceMap = {};

  private subscribedApplicationIds: string[] = [];
  private subscribedProgramIds: string[] = [];

  willDestroy() {
    super.willDestroy();
    if (this.subscribedApplicationIds.length) {
      this.multiDocAppearance.unsubscribe('tas_course_application', this.subscribedApplicationIds);
    }
    if (this.subscribedProgramIds.length) {
      this.multiDocAppearance.unsubscribe('tas_program_instance', this.subscribedProgramIds);
    }
  }

  // Helper to compare arrays regardless of order (for subscription change detection)
  private arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }

  // Remove presence data for IDs that are no longer subscribed
  private removeStalePresenceData(idsToRemove: string[]) {
    const newPresenceData = { ...this.presenceData };
    for (const id of idsToRemove) {
      delete newPresenceData[id];
    }
    this.presenceData = newPresenceData;
  }

  // Subscribe to multi-document appearance when items change
  presenceSubscription = trackedFunction(this, async () => {
    const items = this.args.model?.items;
    if (!items?.length) return;

    // Separate application IDs and program instance IDs
    const applicationIds = items
      .map((item) => item.tasApplicationIdKeyword)
      .filter(Boolean) as string[];

    const programIds = items
      .filter((item) => !item.tasApplicationIdKeyword && item.tasProgramInstanceIdKeyword)
      .map((item) => item.tasProgramInstanceIdKeyword)
      .filter(Boolean) as string[];

    // Cable should already be configured by the instance-initializer at app startup.
    if (!this.cable.isConfigured) {
      const accessToken = this.session.data?.authenticated?.access_token;
      if (!accessToken) {
        // eslint-disable-next-line no-console
        console.error('No access token available for cable configuration');
        return;
      }
      // eslint-disable-next-line no-console
      console.warn('Cable not configured by initializer, configuring now');
      this.cable.configure(ENV.apiHost, ENV.apiKey, accessToken, ENV.wsapiHost);
    }

    // Handle application subscriptions
    const appIdsChanged = !this.arraysEqual(applicationIds, this.subscribedApplicationIds);
    if (appIdsChanged) {
      // Clean up stale presence data from old subscriptions
      if (this.subscribedApplicationIds.length) {
        this.removeStalePresenceData(this.subscribedApplicationIds);
        this.multiDocAppearance.unsubscribe(
          'tas_course_application',
          this.subscribedApplicationIds
        );
      }
      this.subscribedApplicationIds = applicationIds;
      if (applicationIds.length) {
        this.multiDocAppearance.subscribe('tas_course_application', applicationIds, (data) => {
          // Merge incoming data instead of replacing
          this.presenceData = { ...this.presenceData, ...data };
        });
      }
    }

    // Handle program subscriptions
    const progIdsChanged = !this.arraysEqual(programIds, this.subscribedProgramIds);
    if (progIdsChanged) {
      // Clean up stale presence data from old subscriptions
      if (this.subscribedProgramIds.length) {
        this.removeStalePresenceData(this.subscribedProgramIds);
        this.multiDocAppearance.unsubscribe('tas_program_instance', this.subscribedProgramIds);
      }
      this.subscribedProgramIds = programIds;
      if (programIds.length) {
        this.multiDocAppearance.subscribe('tas_program_instance', programIds, (data) => {
          // Merge incoming data instead of replacing
          this.presenceData = { ...this.presenceData, ...data };
        });
      }
    }
  });

  // Get viewers for a specific document ID (works for both applications and programs)
  getViewersForDocument = (application: TasProgramInstanceResult) => {
    const docId = application.tasApplicationIdKeyword || application.tasProgramInstanceIdKeyword;
    if (!docId) return [];
    return this.presenceData[docId] || [];
  };

  get currentPersonId(): string | null | undefined {
    return this.sessionContext.person?.id;
  }

  get currentCompanyId(): string | null {
    return this.sessionContext.currentEmployee.company?.id || null;
  }

  reviewStates = [
    { value: 'Course Review', key: 'courses' },
    { value: 'Program Review', key: 'program' },
    { value: 'Completion Approval', key: 'completion' },
    { value: 'Withdrawal Approval', key: 'withdrawal' },
    { value: 'Evidence Review', key: 'evidence' },
    { value: 'Attachment Review', key: 'assistance' },
  ];

  programOptionsData = trackedFunction(this, async () => {
    if (!this.currentCompanyId) {
      return [];
    }
    return await this.store.query<TasProgramTemplateModel>('tas-program-template', {
      filter: {
        company: this.currentCompanyId,
      },
    });
  });

  get programOptions() {
    return (
      this.programOptionsData.value?.map((program) => {
        return {
          key: program.id,
          value: getSingleValueForTasField('PROGRAM_NAME', program.fields) || '',
        };
      }) || []
    );
  }

  get companyFilters() {
    const companyId = this.sessionContext.currentEmployee.company?.id;
    return companyId ? { companyId } : {};
  }

  didSearch = (string: string) => {
    this.args.controller.search = string;
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = page;
  };

  setSelectedProgramFilter = (selectedKey: string | null) => {
    this.args.controller.program = selectedKey || '';
  };

  onStatusChange = (status: string | null) => {
    this.args.controller.approval = status || '';
  };

  onWaitingOnOthersChange = (checked: boolean) => {
    this.args.controller.waitingOnOthers = checked;
  };

  <template>
    {{pageTitle (t "tuition_assistance.applications.default")}}
    <VStack>
      <Section class="overflow-x-auto">
        <:header>{{t "common.reporting.tas.approval.pending"}} ({{@model.total}})</:header>
        <:subHeader>
          <div class="space-y-3">
            <div class="flex gap-4 items-start">
              <SearchInput
                placeholder="App ID, Employee ID, Email"
                @value={{@controller.search}}
                @onChange={{this.didSearch}}
                @onSearch={{this.didSearch}}
                class="flex-1"
              />
              <Select
                class="w-48 shrink-0"
                @placeholder="By program"
                @items={{this.programOptions}}
                @selected={{@controller.program}}
                @onChange={{this.setSelectedProgramFilter}}
                @clearable={{true}}
              />
              <Select
                class="w-48 shrink-0"
                @placeholder={{t "common.status"}}
                @items={{this.reviewStates}}
                @selected={{@controller.approval}}
                @onChange={{this.onStatusChange}}
                @clearable={{true}}
              />
            </div>
            <div class="flex items-center">
              <Checkbox
                @label="Waiting on others"
                @checked={{@controller.waitingOnOthers}}
                @onChange={{this.onWaitingOnOthersChange}}
              />
            </div>
          </div>
        </:subHeader>
        <:actions>
          <SearchPalette
            @searchTypes={{hash enablePeople=false enableTas=true enablePslf=false}}
            @filters={{this.companyFilters}}
            @routeConfig={{hash
              peopleRoute="authenticated.admin.people.show"
              tasRoute="authenticated.admin.tuition-assistance.program-instances.show"
              tasApplicationRoute="authenticated.admin.tuition-assistance.applications.show"
              pslfRoute="authenticated.admin.pslf.forms"
            }}
          />
        </:actions>
        <:body>
          {{! Consume trackedFunction to trigger presence subscription }}
          {{#if this.presenceSubscription.isResolved}}{{! presence active }}{{/if}}
          <VStack @collapsed={{true}}>
            {{#each @model.items as |application|}}
              <TasApprovalQueueRow
                @application={{application}}
                @personRoute="authenticated.admin.people.show"
                @rootRoute="authenticated.admin"
                @viewers={{this.getViewersForDocument application}}
              />
            {{/each}}
            <Paginator
              @totalItems={{@model.total}}
              @pageSize={{@controller.limit}}
              @currentPage={{@controller.page}}
              @onChange={{this.changePaginatorPage}}
            />
          </VStack>
        </:body>
      </Section>
    </VStack>
  </template>
}
